import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { DiscussionItem } from 'common/workitems/interfaces/discussion-item.interface';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { WorkItemChat } from 'common/workitems/models/work-item-chat';
import { merge } from 'lodash';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { WorkItemTypes, WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { EmailItem } from 'common/workitems/interfaces/email-item.interface';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { VisitsService } from 'common/visits/visits.service';
import { EmailsService } from 'common/emails/emails.service';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { WorkItemHelper } from 'common/workitems/workitem-helper';

@Component({
  selector: 'app-discussion-widget',
  templateUrl: './discussion-widget.component.html',
  styleUrls: ['./discussion-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DiscussionWidgetComponent implements OnInit, OnDestroy {
  @Input()
  workItem: DiscussionItem;

  public discussionThemeClass;
  public subWorkItemThemeClass;
  public chat;
  public subWorkItem: ChatItem | EmailItem;
  public visitor;
  public emailThread;
  public lastEmail;

  private _currentUrlSubscription;

  constructor(private bcVisitorInfoBuilderService: BcVisitorInfoBuilderService,
              private bcVisits: VisitsService,
              private _xlcEmails: EmailsService,
              private _changeDetector: ChangeDetectorRef,
              private _workItemObservableCacheService: WorkItemObservableCacheService) {
    this.discussionThemeClass = 'theme-orange-yellow';
  }

  ngOnInit() {
    switch (this.workItem.WorkItemType) {
      case WorkItemTypeValue.CHAT:
        this._loadChat();
        break;
      case WorkItemTypeValue.EMAIL:
        this._loadEmail();
        break;
    }
  }

  ngOnDestroy() {
    if (this._currentUrlSubscription) {
      this._currentUrlSubscription.unsubscribe();
      this._currentUrlSubscription = null;
    }
  }

  get isWorkItemChat(): boolean {
    return this.workItem && this.workItem.WorkItemType === WorkItemTypeValue.CHAT;
  }

  get isWorkItemEmail(): boolean {
    return this.workItem && this.workItem.WorkItemType === WorkItemTypeValue.EMAIL;
  }

  getSubWorkItemType(): string {
    return this.workItem && WorkItemHelper.convertDbEnumToItemType(this.workItem.WorkItemType);
  }

  private _loadChat(): void {
    this._workItemObservableCacheService.getWorkItemObservable(this.workItem.WorkItemID, WorkItemTypes.CHAT).subscribe(chat => {
      this._setChat(chat);
      this._buildVisitorInfoForChat();
      this._changeDetector.detectChanges();
    });
  }

  private _setChat(chat): void {
    this.chat = chat;
    this.subWorkItem = new WorkItemChat(this.chat);
    this._setThemeClass();
  }

  private _setThemeClass(): void {
    this.subWorkItemThemeClass = ThemeServiceHelper.getCssClass(this.workItem.WorkItemID);
  }

  private _getActualEmailThreadId(): string {
    return this.workItem.WorkItemID;
  }

  private _loadEmail(): void {
    const emailThreadId = this._getActualEmailThreadId();
    if (emailThreadId) {
      Promise.all([
        this._loadThreadInfo(emailThreadId),
        this._loadLastEmailFromThread(emailThreadId)
      ]).then(() => {
        this._changeDetector.detectChanges();
      });
    }
  }

  private _loadThreadInfo(emailThreadId): Promise<void> {
    return this._xlcEmails.getEmailThread(emailThreadId)
      .then(response => this._setEmailThread(response))
      .then(() => {
        this._buildVisitorInfoForEmail();
        this._setThemeClass();
      });
  }

  private _setEmailThread(emailThread): void {
    this.emailThread = emailThread;
  }

  private _loadLastEmailFromThread(emailThreadId: string): Promise<any> {
    this.lastEmail = null;
    return this._xlcEmails.getLastEmailFromThreadWithAttachments(emailThreadId)
      .then(email => {
        this._setEmail(email);
        return email;
      });
  }

  private _setEmail(email): void {
    this.lastEmail = email;
  }

  private _buildVisitorInfoForChat(): Promise<void> {
    return Promise.all([
      this.bcVisitorInfoBuilderService.getVisitorInfoModelFromChat(this.chat),
      this.bcVisitorInfoBuilderService.getFolderName(this.chat),
      this.bcVisitorInfoBuilderService.getDepartmentName(this.chat),
      this.bcVisitorInfoBuilderService.getWebsite(this.chat)
    ]).then((result) => {
      this.visitor = merge({}, this.visitor, result[0]);
      this.visitor.fields.folderName.value = result[1];
      this.visitor.fields.departmentName.value = result[2];
      this.visitor.fields.website.value = result[3];
      this._createCurrentUrlObservable();
    });
  }

  private _buildVisitorInfoForEmail(): Promise<void> {
    return this.bcVisitorInfoBuilderService.createVisitorDataForEmail(this.emailThread).then(visitor => {
        this.visitor = visitor;
        this._setDepartmentName();
      }
    );
  }

  private _setDepartmentName(): Promise<void> {
    if (this.emailThread.departmentName) {
      this.visitor.fields.departmentName.value = this.emailThread.departmentName;
      return;
    }
    return this.bcVisitorInfoBuilderService.getDepartmentName(this.emailThread)
      .then(departmentName => {
        this.visitor.fields.departmentName.value = departmentName;
      });
  }

  private _createCurrentUrlObservable(): void {
    if (!this._currentUrlSubscription && this.chat.VisitID && this.chat.VisitorID) {
      this._currentUrlSubscription = this.bcVisits.observeVisitInfoByVisitId(this.chat.VisitID, this.chat.VisitorID).subscribe(visit => {
        this.visitor.fields.currentUrl.value = visit.data.CurrentUrl;
      });
    }
  }
}
